import { t } from 'i18next'
import { isClient } from '@shared/index.tsx'
import { syncTranslation } from '@/client/utils/translate'
import { user as authUser } from '@/client/stores/authStore'
import { Capabilities, hasPermissionFor, hasRoleOf, RoleType, roles } from './roles.core'

//
// The roles/capabilities logic itself lives in './roles.core', which has no
// imports at all. This module adds the client-side conveniences on top: the
// translated role list and the two helpers that fall back to the logged-in user
// when no role is passed.
//
// Server code should import from '@shared/roles.core' directly — importing this
// module pulls in i18next, the client auth store and @shared/index.tsx (and with
// it prettier and JSX).
//
export * from './roles.core'

/**
 * List of roles as value/label pair
 */
type ValuePairProps = { value: string; label: string }
let roleTypeValues: ValuePairProps[] = []
syncTranslation(() => {
    // translate the roles to the current language
    roleTypeValues = Object.entries(RoleType).map(([label, value]) => ({
        label: t(label),
        value,
    }))
})
export const roleTypeList = (): ValuePairProps[] => {
    const assignableRoles = roles.find((item) => item.role === authUser?.value.role)?.canAssignRoles
    return roleTypeValues.filter((roleTypeValue) => assignableRoles?.includes(roleTypeValue.value as RoleType))
}

/**
 * Returns true if the role contains the capability cap.
 * On the client the role defaults to the logged-in user's role.
 * @param cap
 * @param role
 * @returns
 */
export const hasPermission = (cap: Capabilities, role: RoleType | null = null) => {
    if (isClient && authUser) {
        if (!role) role = authUser?.value.role
    }
    return hasPermissionFor(cap, role)
}

/**
 * Returns true if user role is in the specified roles.
 * If userRole == null, the role of the logged in user will be used
 * @param allowedRoles
 * @param userRole
 * @returns
 */
export const hasRole = (allowedRoles: RoleType | RoleType[], userRole: RoleType | null = null) => {
    if (isClient && authUser && !userRole) {
        userRole = authUser?.value.role
    }
    return hasRoleOf(allowedRoles, userRole)
}
